import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useChat } from '../contexts/ChatContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  User, 
  LogOut, 
  Trash2, 
  Bot,
  Crown,
  Settings,
  Code,
  Sparkles,
  Sun,
  Moon,
  Search,
  Filter,
  Grid,
  List,
  MoreVertical,
  Edit,
  Star,
  Clock,
  X,
  MessageSquare,
  Image,
  Video,
  FileText,
  Zap,
  Brain,
  Award,
  Globe,
  Eye,
  Shield,
  Lock,
  Unlock,
  Bell,
  Mail,
  Home,
  User as UserIcon,
  Edit as EditIcon,
  Save,
  Check,
  AlertCircle,
  Info,
  HelpCircle,
  ExternalLink,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  Menu,
  X as XIcon,
  Search as SearchIcon,
  Filter as FilterIcon,
  SortAsc,
  SortDesc,
  RefreshCw,
  RefreshCcw,
  RotateCw,
  RotateCcw as RotateCcwIcon,
  ZoomIn,
  ZoomOut,
  Move,
  Crop,
  Scissors,
  Paintbrush,
  Eraser,
  Pen,
  Pencil,
  Highlighter,
  Type,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  List as ListIcon,
  ListOrdered,
  Quote,
  Code as CodeIcon,
  Terminal,
  Database,
  Server,
  Cloud,
  CloudOff,
  Wifi,
  WifiOff,
  Signal,
  SignalZero,
  SignalOne,
  SignalTwo,
  SignalThree,
  Battery,
  BatteryLow,
  BatteryMedium,
  BatteryHigh,
  BatteryFull,
  Plug,
  Unplug,
  Power,
  PowerOff,
  PlayCircle,
  PauseCircle,
  StopCircle,
  SkipBack,
  SkipForward,
  FastForward,
  Rewind,
  Shuffle,
  Repeat,
  RepeatOne,
  Volume1,
  Volume2 as Volume2Icon,
  VolumeX as VolumeXIcon,
  Headphones,
  Speaker,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Video as VideoIcon,
  VideoOff as VideoOffIcon,
  Camera as CameraIcon,
  Image as ImageIcon,
  File,
  Folder,
  FolderOpen,
  FolderPlus,
  FolderMinus,
  FolderX,
  FilePlus,
  FileMinus,
  FileX,
  FileText as FileTextIcon,
  FileImage,
  FileVideo,
  FileAudio,
  FileArchive,
  FileCode,
  FileSpreadsheet,
  FilePdf,
  FileWord,
  FilePowerpoint,
  FileExcel,
  FileZip,
  FileJson,
  FileXml,
  FileCsv,
  FileTxt,
  FileMd,
  FileHtml,
  FileCss,
  FileJs,
  FileTs,
  FileJsx,
  FileTsx,
  FileVue,
  FileSvelte,
  FileAngular,
  FileReact,
  FileNode,
  FilePython,
  FileJava,
  FileC,
  FileCpp,
  FileCsharp,
  FilePhp,
  FileRuby,
  FileGo,
  FileRust,
  FileSwift,
  FileKotlin,
  FileDart,
  FileR,
  FileScala,
  FileHaskell,
  FileClojure,
  FileLua,
  FilePerl,
  FileBash,
  FilePowershell,
  FileYaml,
  FileToml,
  FileIni,
  FileEnv,
  FileGitignore,
  FileDocker,
  FileKubernetes,
  FileTerraform,
  FileAnsible,
  FileJenkins,
  FileGithub,
  FileGitlab,
  FileBitbucket,
  FileAzure,
  FileAws,
  FileGcp,
  FileDigitalocean,
  FileHeroku,
  FileVercel,
  FileNetlify,
  FileSurge,
  FileFirebase,
  FileSupabase,
  FilePlanetscale,
  FileMongodb,
  FilePostgresql,
  FileMysql,
  FileRedis,
  FileElasticsearch,
  FileKibana,
  FileGrafana,
  FilePrometheus,
  FileJaeger,
  FileZipkin,
  FileSentry,
  FileLogrocket,
  FileMixpanel,
  FileAmplitude,
  FileSegment,
  FileRudderstack,
  FileSnowflake,
  FileBigquery,
  FileRedshift,
  FileS3,
  FileCloudfront,
  FileRoute53,
  FileCloudflare,
  FileFastly,
  FileAkamai,
  FileMaxcdn,
  FileKeycdn,
  FileBunnycdn,
  FileStackpath,
  FileCloudinary,
  FileImgix,
  FileImagekit,
  FileUploadcare,
  FileFilestack,
  FileDropzone,
  FileTus,
  FileResumable,
  FileChunked,
  FileStreaming,
  FileProgressive,
  FileLazy,
  FileEager,
  FileOptimized,
  FileCompressed,
  FileMinified,
  FileBundled,
  FileTranspiled,
  FilePolyfilled,
  FileShimmed,
  FilePonyfilled,
  FileProlyfilled,
  FilePolyfilled as PolyfilledIcon,
  FileShimmed as ShimmedIcon,
  FilePonyfilled as PonyfilledIcon,
  FileProlyfilled as ProlyfilledIcon
} from 'lucide-react';
import toast from 'react-hot-toast';

const ModernDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { chats, fetchChats, createChat, deleteChat } = useChat();
  
  const [selectedModel, setSelectedModel] = useState('gpt-3.5-turbo');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredChats, setFilteredChats] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [showFilters, setShowFilters] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [filters, setFilters] = useState({
    date: 'all',
    type: 'all',
    status: 'all'
  });
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [newChatTitle, setNewChatTitle] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Responsive breakpoint kontrolü
  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // AI Models Configuration
  const aiModels = [
    {
      id: 'gpt-3.5-turbo',
      name: 'GPT-3.5 Turbo',
      description: 'Hızlı ve etkili sohbet',
      icon: Zap,
      color: 'bg-green-500',
      available: true,
      premium: false
    },
    {
      id: 'gpt-4',
      name: 'GPT-4',
      description: 'En gelişmiş OpenAI modeli',
      icon: Brain,
      color: 'bg-purple-500',
      available: user?.is_premium,
      premium: true
    },
    {
      id: 'gpt-4-turbo',
      name: 'GPT-4 Turbo',
      description: 'Hızlı GPT-4 versiyonu',
      icon: Sparkles,
      color: 'bg-indigo-500',
      available: user?.is_premium,
      premium: true
    },
    {
      id: 'claude-3-haiku',
      name: 'Claude 3 Haiku',
      description: 'Hızlı ve verimli Claude',
      icon: Bot,
      color: 'bg-orange-500',
      available: true,
      premium: false
    },
    {
      id: 'claude-3-sonnet',
      name: 'Claude 3 Sonnet',
      description: 'Güçlü Claude modeli',
      icon: Crown,
      color: 'bg-yellow-500',
      available: user?.is_premium,
      premium: true
    },
    {
      id: 'claude-3-opus',
      name: 'Claude 3 Opus',
      description: 'En gelişmiş Claude modeli',
      icon: Award,
      color: 'bg-red-500',
      available: user?.is_premium,
      premium: true
    },
    {
      id: 'gemini-pro',
      name: 'Gemini Pro',
      description: 'Google\'ın gelişmiş modeli',
      icon: Globe,
      color: 'bg-blue-500',
      available: true,
      premium: false
    },
    {
      id: 'gemini-pro-vision',
      name: 'Gemini Pro Vision',
      description: 'Görsel anlayışlı Gemini',
      icon: Eye,
      color: 'bg-cyan-500',
      available: user?.is_premium,
      premium: true
    }
  ];

  useEffect(() => {
    fetchChats();
  }, [fetchChats]);

  useEffect(() => {
    if (searchQuery) {
      const filtered = chats.filter(chat => 
        chat.title.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredChats(filtered);
    } else {
      setFilteredChats(chats);
    }
  }, [searchQuery, chats]);

  const handleNewChat = async () => {
    if (!newChatTitle.trim()) {
      toast.error('Sohbet başlığı gerekli');
      return;
    }

    try {
      const newChat = await createChat(newChatTitle.trim());
      if (newChat) {
        navigate(`/chat/${newChat.id}`);
        setShowNewChatModal(false);
        setNewChatTitle('');
      }
    } catch (error) {
      // Error creating chat
      toast.error('Yeni sohbet oluşturulamadı');
    }
  };

  const handleDeleteChat = async (chatId, e) => {
    e.stopPropagation();
    if (!window.confirm('Bu sohbeti silmek istediğinizden emin misiniz?')) {
      return;
    }
    
    try {
      await deleteChat(chatId);
      toast.success('Sohbet silindi');
    } catch (error) {
      // Error deleting chat
      const errorMessage = error.message || 'Sohbet silinemedi';
      toast.error(`Hata: ${errorMessage}`);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) {
      return 'Dün';
    } else if (diffDays < 7) {
      return `${diffDays} gün önce`;
    } else {
      return date.toLocaleDateString('tr-TR');
    }
  };

  const getModelIcon = (modelId) => {
    const model = aiModels.find(m => m.id === modelId);
    return model ? model.icon : Bot;
  };

  const getModelColor = (modelId) => {
    const model = aiModels.find(m => m.id === modelId);
    return model ? model.color : 'bg-gray-500';
  };

  const renderChatCard = (chat) => {
    const Icon = getModelIcon(selectedModel);
    const colorClass = getModelColor(selectedModel);

    return (
      <motion.div
        key={chat.id}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => navigate(`/chat/${chat.id}`)}
        className="group relative bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-200 cursor-pointer overflow-hidden"
      >
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className={`w-10 h-10 rounded-xl ${colorClass} flex items-center justify-center`}>
                <Icon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                  {chat.title}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {formatDate(chat.created_at)}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={(e) => handleDeleteChat(chat.id, e)}
                className="p-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              
              <button className="p-2 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                <MoreVertical className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <MessageSquare className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {chat.message_count || 0} mesaj
              </span>
            </div>
            
            <div className="flex items-center space-x-1">
              {chat.is_premium && (
                <Crown className="w-4 h-4 text-yellow-500" />
              )}
              <Clock className="w-4 h-4 text-gray-400" />
            </div>
          </div>
        </div>
        
        <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-blue-500 to-purple-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-200" />
      </motion.div>
    );
  };

  const renderChatList = (chat) => {
    const Icon = getModelIcon(selectedModel);
    const colorClass = getModelColor(selectedModel);

    return (
      <motion.div
        key={chat.id}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 20 }}
        onClick={() => navigate(`/chat/${chat.id}`)}
        className="group flex items-center space-x-4 p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-200 cursor-pointer"
      >
        <div className={`w-10 h-10 rounded-xl ${colorClass} flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors truncate">
            {chat.title}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
            {formatDate(chat.created_at)} • {chat.message_count || 0} mesaj
          </p>
        </div>
        
        <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => handleDeleteChat(chat.id, e)}
            className="p-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          
          <button className="p-2 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
            <MoreVertical className="w-4 h-4" />
          </button>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="h-screen gokturk-bg overflow-hidden">
      {/* Header */}
      <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-b border-gray-200/50 dark:border-gray-700/50 p-6 h-20 flex items-center">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                <Bot className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  TurkGPT
                </h1>
                <p className="text-gray-500 dark:text-gray-400">
                  Hoş geldin, {user?.name || 'Kullanıcı'}!
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowProfile(true)}
                className="p-2 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                <User className="w-5 h-5" />
              </button>
              
              <button
                onClick={() => setShowSettings(true)}
                className="p-2 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                <Settings className="w-5 h-5" />
              </button>
              
              <button
                onClick={logout}
                className="p-2 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-6">
        {/* Search and Filters */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Sohbetlerim
              </h2>
              <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-full text-sm font-medium">
                {filteredChats.length} sohbet
              </span>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="p-2 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                <Filter className="w-5 h-5" />
              </button>
              
              <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'grid' 
                      ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400' 
                      : 'text-gray-500 dark:text-gray-400'
                  }`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'list' 
                      ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400' 
                      : 'text-gray-500 dark:text-gray-400'
                  }`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Sohbetlerde ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <button
              onClick={() => setShowNewChatModal(true)}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-200 flex items-center space-x-2 font-medium"
            >
              <Plus className="w-5 h-5" />
              <span>Yeni Sohbet</span>
            </button>
          </div>
        </div>

        {/* Chats Grid/List */}
        {filteredChats.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
              <MessageSquare className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Henüz sohbet yok
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              İlk sohbetinizi başlatmak için yeni sohbet oluşturun
            </p>
            <button
              onClick={() => setShowNewChatModal(true)}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-200 flex items-center space-x-2 font-medium mx-auto"
            >
              <Plus className="w-5 h-5" />
              <span>Yeni Sohbet Oluştur</span>
            </button>
          </div>
        ) : (
          <div className={
            viewMode === 'grid' 
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6' 
              : 'space-y-4'
          }>
            <AnimatePresence>
              {filteredChats.map(chat => 
                viewMode === 'grid' ? renderChatCard(chat) : renderChatList(chat)
              )}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* New Chat Modal */}
      <AnimatePresence>
        {showNewChatModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Yeni Sohbet
                </h3>
                <button
                  onClick={() => setShowNewChatModal(false)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Sohbet Başlığı
                  </label>
                  <input
                    type="text"
                    value={newChatTitle}
                    onChange={(e) => setNewChatTitle(e.target.value)}
                    placeholder="Sohbet başlığını girin..."
                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    AI Modeli
                  </label>
                  <select
                    value={selectedModel}
                    onChange={(e) => setSelectedModel(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {aiModels.map(model => (
                      <option key={model.id} value={model.id} disabled={!model.available}>
                        {model.name} {!model.available ? '(Premium gerekli)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowNewChatModal(false)}
                  className="px-4 py-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={handleNewChat}
                  disabled={!newChatTitle.trim()}
                  className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Oluştur
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Profile Modal */}
        {showProfile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowProfile(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Profil Bilgileri
                </h2>
                <button
                  onClick={() => setShowProfile(false)}
                  className="p-2 rounded-xl bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-xl font-bold">
                    {user?.name?.charAt(0)?.toUpperCase() || 'M'}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {user?.name || 'Misafir Kullanıcı'}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {user?.email || 'guest@example.com'}
                    </p>
                    <span className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full mt-1">
                      {user?.user_type === 'guest' ? 'Misafir' : 
                       user?.user_type === 'google' ? 'Google' : 
                       user?.user_type === 'email' ? 'Email' : 'Kullanıcı'}
                    </span>
                  </div>
                </div>

                <div className="border-t dark:border-gray-700 pt-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Kullanıcı Tipi:</span>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {user?.user_type === 'guest' ? 'Misafir' : 
                         user?.user_type === 'google' ? 'Google' : 
                         user?.user_type === 'email' ? 'Email' : 'Bilinmiyor'}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Premium Durumu:</span>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {user?.is_premium ? 'Premium' : 'Ücretsiz'}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Mesaj Limiti:</span>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {user?.user_type === 'guest' ? '50/gün' : 
                         user?.user_type === 'google' ? '100/gün' : 
                         user?.is_premium ? 'Sınırsız' : '100/gün'}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Görsel Limiti:</span>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {user?.user_type === 'guest' ? '0/gün' : 
                         user?.user_type === 'google' ? '5/gün' : 
                         user?.is_premium ? 'Sınırsız' : '5/gün'}
                      </p>
                    </div>
                  </div>
                </div>

                {user?.user_type === 'guest' && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                          Misafir Kullanıcı
                        </h4>
                        <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                          Daha fazla özellik için Google ile giriş yapın veya hesap oluşturun.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowProfile(false)}
                  className="px-4 py-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                >
                  Kapat
                </button>
                {user?.user_type === 'guest' && (
                  <button
                    onClick={() => {
                      setShowProfile(false);
                      // Navigate to login page using React Router
                      navigate('/login');
                    }}
                    className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-200"
                  >
                    Giriş Yap
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Settings Modal */}
        {showSettings && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowSettings(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Ayarlar
                </h2>
                <button
                  onClick={() => setShowSettings(false)}
                  className="p-2 rounded-xl bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Tema Seçimi */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tema
                  </label>
                  <select 
                    value={user?.theme || 'default'}
                    onChange={(e) => {
                      // Tema değiştirme işlemi
                      // TODO: Implement theme change functionality
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="default">Varsayılan</option>
                    <option value="dark">Koyu</option>
                    <option value="light">Açık</option>
                    <option value="blue">Mavi</option>
                    <option value="purple">Mor</option>
                  </select>
                </div>

                {/* Dil Seçimi */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Dil
                  </label>
                  <select 
                    defaultValue="tr"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="tr">Türkçe</option>
                    <option value="en">English</option>
                    <option value="de">Deutsch</option>
                    <option value="fr">Français</option>
                  </select>
                </div>

                {/* Bildirim Ayarları */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Bildirimler
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input 
                        type="checkbox" 
                        defaultChecked
                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        Mesaj bildirimleri
                      </span>
                    </label>
                    <label className="flex items-center">
                      <input 
                        type="checkbox" 
                        defaultChecked
                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        Sistem bildirimleri
                      </span>
                    </label>
                    <label className="flex items-center">
                      <input 
                        type="checkbox"
                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        E-posta bildirimleri
                      </span>
                    </label>
                  </div>
                </div>

                {/* Gizlilik Ayarları */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Gizlilik
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input 
                        type="checkbox" 
                        defaultChecked
                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        Çevrimiçi durumumu göster
                      </span>
                    </label>
                    <label className="flex items-center">
                      <input 
                        type="checkbox"
                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        Profilimi gizli yap
                      </span>
                    </label>
                  </div>
                </div>

                {user?.user_type === 'guest' && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <Info className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                          Misafir Kullanıcı
                        </h4>
                        <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                          Ayarlarınız geçici olarak saklanır. Kalıcı ayarlar için hesap oluşturun.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowSettings(false)}
                  className="px-4 py-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={() => {
                    setShowSettings(false);
                    // Ayarları kaydet
                    // TODO: Implement settings save functionality
                  }}
                  className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-200"
                >
                  Kaydet
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ModernDashboard;
